import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MapPin, Search as SearchIcon, Loader2, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { getSportColor } from "@/lib/sports";
import { useI18n } from "@/lib/i18n";
import SportAutocomplete from "@/components/SportAutocomplete";
import LocationAutocomplete from "@/components/LocationAutocomplete";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState(searchParams.get("sport_type") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const navigate = useNavigate();
  const { t } = useI18n();

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sport) params.sport_type = sport;
      if (city) params.city = city;
      if (query) params.q = query;
      const res = await api.get("/venues", { params });
      setVenues(res.data);
    } catch { setVenues([]); }
    setLoading(false);
  };

  useEffect(() => { fetchVenues(); }, []); // eslint-disable-line

  const handleSearch = () => {
    const p = new URLSearchParams();
    if (sport) p.set("sport_type", sport);
    if (city) p.set("city", city);
    if (query) p.set("q", query);
    setSearchParams(p);
    fetchVenues();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6" data-testid="search-page-title">
        {t("search.findCourts")}
      </h1>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-8 p-4 bg-white border-2 border-slate-200" data-testid="search-filters">
        <div className="flex-1">
          <Input data-testid="search-query" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search venues..." className="h-11 rounded-none border-2" onKeyDown={e => e.key === "Enter" && handleSearch()} />
        </div>
        <SportAutocomplete value={sport} onValueChange={setSport} placeholder="All Sports" triggerClassName="w-full md:w-52" />
        <LocationAutocomplete value={city} onValueChange={setCity} placeholder="All Locations" triggerClassName="w-full md:w-56" />
        <Button data-testid="apply-filters-btn" onClick={handleSearch}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-8 h-11 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none">
          <SearchIcon className="w-4 h-4 mr-2" />Search
        </Button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>
      ) : venues.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-heading text-xl text-muted-foreground uppercase">{t("search.noVenuesFound")}</p>
          <p className="text-sm text-muted-foreground mt-2">{t("search.tryAdjusting")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="venues-grid">
          {venues.map((v, i) => (
            <motion.div key={v.venue_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/venues/${v.venue_id}`)}
              className="bg-white border-b-4 border-r-4 border-black overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-200"
              data-testid={`venue-card-${v.venue_id}`}>
              <div className="h-48 overflow-hidden">
                <img src={v.images?.[0] || "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600"} alt={v.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
              </div>
              <div className="p-4">
                <div className="flex gap-2 mb-2 flex-wrap">
                  {v.sport_types?.map(s => (
                    <Badge key={s} variant="outline" className={`text-[10px] font-heading uppercase tracking-wider ${getSportColor(s)}`}>{s}</Badge>
                  ))}
                </div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight">{v.name}</h3>
                <p className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                  <MapPin className="w-3 h-3" />{v.city}
                </p>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{v.description}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted-foreground">{v.total_courts || (v.resources?.length || 0)} court{(v.total_courts || v.resources?.length || 0) !== 1 ? 's' : ''} / space{(v.total_courts || v.resources?.length || 0) !== 1 ? 's' : ''} available</p>
                  {v.esg_grade && (
                    <Badge className="bg-green-600 text-white text-[9px]"><Leaf className="w-2.5 h-2.5 mr-0.5" />ESG {v.esg_grade}</Badge>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
